import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Chip } from '@/new/components/chip';
import { DocumentViewer } from '@/new/components/doc-viewer/document-viewer';
import { Icon } from '@/new/components/icon';
import { buildFieldsArray, renderFieldValue, RenderFieldValueOptions } from '@/new/components/metadata-display';
import { DrawerErrorState } from '@/new/components/reference-content/drawer-error-state';
import { ReferenceInfoPanel } from '@/new/components/reference-content/reference-info-panel';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';

interface DocumentDrawerContentProps {
  docId: string;
  onClose?: () => void;
}

export function DocumentDrawerContent({ docId, onClose }: DocumentDrawerContentProps) {
  const { matterId } = useParams();
  const { data: matter, isLoading: isLoadingMatter } = useGetMatterV2(matterId);
  const subsetId = matter?.allDocumentsCaseId ?? undefined;
  const { data: doc, isLoading: isLoadingDoc, error } = useGetDoc(matterId, docId);
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);

  const metadataFields = buildFieldsArray(doc?.file?.fileMetadata?.relativityFields ?? {});
  const fieldOptions: RenderFieldValueOptions = {
    badgeClassName: 'text-[11px] bg-gray-100 text-gray-600 py-px border border-gray-200',
    emptyText: 'None',
    maxTextLength: Infinity,
    compact: true,
  };

  return (
    <div className="flex h-full flex-col">
      {error ? (
        <DrawerErrorState label="document" />
      ) : (
        <>
          {(isLoadingMatter || isLoadingDoc) && (
            <div className="my-20 flex h-full w-full items-center justify-center">
              <Icon className="lucide--loader-2 animate-spin duration-700" />
            </div>
          )}
          {doc && subsetId && (
            <>
              {/* Document Viewer Section */}
              <div className="-mb-8 flex-1 overflow-hidden">
                <div className="relative h-full">
                  <DocumentViewer
                    matterId={matterId}
                    fileExtension={doc.file?.extension}
                    docId={docId}
                    highlights={[]}
                    hideFeatureButtons={true}
                    hideSnippingTool={true}
                    fileName={doc.file?.name}
                    closeViewer={onClose}
                  />
                </div>
              </div>

              <ReferenceInfoPanel
                key={docId}
                tabs={[{ id: docId, label: doc.file?.name ?? '' }]}
                selectedTabId={docId}
                onSelectTab={() => {}}
                isCollapsed={isInfoPanelCollapsed}
                onToggleCollapsed={() => setIsInfoPanelCollapsed((collapsed) => !collapsed)}
                showToggle={!!(doc.summary || metadataFields.length > 0)}
                expandableContent={
                  metadataFields.length > 0 ? (
                    <div className="w-full space-y-2 pb-5">
                      <div className="mb-4 flex items-center gap-1.5 text-xs font-medium text-purple-500 uppercase">
                        <Icon className="lucide--database mb-px" />
                        <span>Relativity Metadata</span>
                      </div>

                      {metadataFields.map((field) => (
                        <div key={field.label} className="flex gap-2">
                          <span className="w-28 shrink-0 text-xs text-gray-500">{field.label}</span>
                          <span className="min-w-0 flex-1">{renderFieldValue(field, fieldOptions)}</span>
                        </div>
                      ))}
                    </div>
                  ) : undefined
                }
              >
                {(isExpanded) => (
                  <>
                    <div className="my-2 mt-3 flex items-center justify-between gap-2">
                      <p className="font-semibold break-all text-gray-700">{doc.title ?? doc.file?.name}</p>

                      {doc.type && <Chip className={CHIP_COLOURS.type}>{doc.type}</Chip>}
                    </div>

                    {doc.summary && <p className={cn('text-gray-700', !isExpanded && 'line-clamp-4')}>{doc.summary}</p>}
                  </>
                )}
              </ReferenceInfoPanel>
            </>
          )}
        </>
      )}
    </div>
  );
}
